/**
 * ActionCardContent — standard content wrapper for v2 action cards.
 *
 * Provides the consistent `px-4 py-3 space-y-3` padding every action card
 * content area uses, plus an optional `contextMessage` slot that renders a
 * compact MarkdownContent block **after** `children`.
 *
 * Only use the `contextMessage` prop when the context message was originally
 * rendered last (i.e. after all other content). This matches `CreateActionCard`,
 * where the message naturally belongs at the bottom.
 *
 * For call sites where the context message should appear between other elements
 * (e.g. after the entity preview but before a DiffSection),
 * render `<MarkdownContent>` inline at the correct position instead of using
 * this prop.
 *
 * Usage (context message last — CreateActionCard pattern):
 *   <ActionCardContent contextMessage={payload.context_message} orgId={orgId}>
 *     <EntityPreview ... />
 *   </ActionCardContent>
 *
 * Usage (context message between children — inline pattern):
 *   <ActionCardContent>
 *     <EntityPreview ... />
 *     {payload.context_message && (
 *       <MarkdownContent className='text-sm text-fg-secondary'
 *         content={payload.context_message} orgId={orgId} size='compact' />
 *     )}
 *     <DiffSection ... />
 *   </ActionCardContent>
 */

import type { ReactNode } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { cx } from '@/utils/cx';

import { ACTION_CARD_BODY_CLASS } from './actionCardLayout';

interface ActionCardContentProps {
  children: ReactNode;
  /** Optional AI reasoning context rendered below the content. */
  contextMessage?: string | null;
  /** Org ID for entity-link resolution inside the context message. */
  orgId?: number;
  /** Additional classes merged onto the outer container. */
  className?: string;
  /**
   * Meeting-slot ids pre-resolved to times (from the action's
   * `enriched_data.resolved_slots`), passed through to the context-message
   * markdown renderer so meeting-slot chips render without a network call.
   */
  resolvedSlots?: Record<string, { start: string; end: string; label: string }>;
}

/**
 * Thin wrapper providing the standard action card content padding and an
 * optional context-message slot. Replaces the repeated
 * `<div className='px-4 py-3 space-y-3'> ... {msg && <MarkdownContent />}</div>`
 * pattern across every action card.
 *
 * Content is capped at 500px: the action card shell spans the full proposal
 * column (up to 1000px), but entity previews / diff rows / context messages
 * read best at a narrower measure. In a wide column the content gets an
 * extra left/bottom margin so it sits slightly inset rather than hugging
 * the card edge — deliberately not centered, but not hard-left either.
 *
 * The inset is a CONTAINER query (`@xl:`), not a viewport one, sizing
 * against the `@container` declared by DetailPanelCard. Card content is
 * shared verbatim by the full-page action route (~1000px column), the
 * legacy actions-centre detail pane (resizable) and the record sidebar
 * (min 400px). A viewport-keyed `lg:` inset fires on any wide screen
 * regardless of how narrow the column actually is, so a 400px sidebar on a
 * desktop monitor would lose 30px it has no room for. `@xl` is 36rem/576px,
 * comfortably clearing the 500px content cap plus the 30px inset.
 */
export function ActionCardContent({
  children,
  contextMessage,
  orgId,
  className,
  resolvedSlots,
}: ActionCardContentProps) {
  return (
    <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-3', className)}>
      {children}
      {contextMessage && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={contextMessage}
          orgId={orgId}
          size='compact'
          resolvedSlots={resolvedSlots}
        />
      )}
    </div>
  );
}
